import { isRegistrationActivationReady } from "../config/registration-state.js";
import { TERMS_CONTENT } from "../config/terms-content.js";
import {
  escapeLegalText,
  isEffectiveTermsPolicy,
  termsDocumentSectionsMarkup,
  termsPolicyDescriptor,
} from "./terms-document-markup.js";

function markup(terms) {
  const copy = terms.signupConsent;
  return `
    <dialog class="terms-review-dialog" data-terms-dialog data-policy-effective="${isEffectiveTermsPolicy(terms)}" aria-labelledby="terms-review-title" aria-describedby="terms-review-description terms-review-status" aria-modal="true">
      <div class="terms-review-dialog__frame">
        <header class="terms-review-dialog__header">
          <div>
            <p class="terms-review-dialog__eyebrow"><i aria-hidden="true"></i> ACCOUNT CREATION // LEGAL CHECKPOINT</p>
            <h2 id="terms-review-title" data-terms-dialog-title>${escapeLegalText(copy.previewTitle)}</h2>
            <p id="terms-review-description">${escapeLegalText(copy.description)}</p>
          </div>
          <button type="button" class="terms-review-dialog__close" data-terms-dialog-close aria-label="${escapeLegalText(copy.closeLabel)}">
            <span aria-hidden="true"></span>
          </button>
        </header>

        <dl class="terms-review-dialog__policy" aria-label="Terms policy identity">
          <div><dt>Policy</dt><dd>${escapeLegalText(terms.policyId)}</dd></div>
          <div><dt>Version</dt><dd>${escapeLegalText(terms.version)}</dd></div>
          <div><dt>Revision</dt><dd>${escapeLegalText(terms.contentRevision)}</dd></div>
          <div class="terms-review-dialog__effective" data-policy-state="${isEffectiveTermsPolicy(terms) ? "effective" : "inactive"}">
            <dt>Effective</dt>
            <dd><strong>${isEffectiveTermsPolicy(terms) ? "In effect" : "Not effective"}</strong><time datetime="${escapeLegalText(terms.effectiveDateIso || "")}">${escapeLegalText(terms.effectiveDate)}</time></dd>
          </div>
        </dl>

        <p class="terms-review-dialog__notice" role="note" data-terms-dialog-notice>${escapeLegalText(copy.previewNotice)}</p>

        <article class="terms-review-dialog__document" role="document" tabindex="0" aria-label="Complete Terms of Service">
          ${termsDocumentSectionsMarkup(terms, { idPrefix: "signup-terms", headingLevel: 3 })}
        </article>

        <footer class="terms-review-dialog__footer">
          <label class="terms-review-dialog__consent" data-terms-consent-row aria-disabled="true">
            <input type="checkbox" name="acceptTerms" value="true" data-terms-consent disabled>
            <span class="terms-review-dialog__checkbox" aria-hidden="true"><i></i></span>
            <strong>${escapeLegalText(copy.checkboxLabel)}</strong>
          </label>
          <p class="terms-review-dialog__status" id="terms-review-status" role="status" aria-live="polite" aria-atomic="true" data-terms-dialog-status>${escapeLegalText(copy.previewNotice)}</p>
          <div class="terms-review-dialog__actions">
            <a href="/terms/" target="_blank" rel="noopener">Open standalone Terms</a>
            <button type="button" class="button button--secondary" data-terms-dialog-cancel>${escapeLegalText(copy.cancelLabel)}</button>
            <button type="button" class="button button--primary" data-terms-dialog-confirm disabled>${escapeLegalText(copy.lockedActionLabel)}</button>
          </div>
        </footer>
      </div>
    </dialog>`;
}

export function createTermsReviewDialog({
  mount,
  terms = TERMS_CONTENT,
  registration,
  onConfirm,
} = {}) {
  if (!(mount instanceof Element)) throw new TypeError("createTermsReviewDialog requires a mount Element.");
  if (!registration || typeof registration !== "object") throw new TypeError("createTermsReviewDialog requires an explicit registration state.");
  if (!terms?.signupConsent || !Array.isArray(terms.sections)) throw new TypeError("createTermsReviewDialog requires configurable Terms content.");

  const host = document.createElement("div");
  host.className = "terms-review-dialog-host";
  host.innerHTML = markup(terms);
  mount.replaceChildren(host);

  const dialog = host.querySelector("[data-terms-dialog]");
  const title = host.querySelector("[data-terms-dialog-title]");
  const notice = host.querySelector("[data-terms-dialog-notice]");
  const status = host.querySelector("[data-terms-dialog-status]");
  const checkbox = host.querySelector("[data-terms-consent]");
  const consentRow = host.querySelector("[data-terms-consent-row]");
  const confirm = host.querySelector("[data-terms-dialog-confirm]");
  const closeButton = host.querySelector("[data-terms-dialog-close]");
  const cancelButton = host.querySelector("[data-terms-dialog-cancel]");
  const descriptor = termsPolicyDescriptor(terms);
  const copy = terms.signupConsent;

  let mode = "preview";
  let explicitAcceptance = false;
  let attempted = false;
  let inFlight = false;
  let invoker = null;
  let destroyed = false;

  function bindingAvailable() {
    return Boolean(
      typeof onConfirm === "function"
      && isRegistrationActivationReady(registration)
      && isEffectiveTermsPolicy(terms)
    );
  }

  function resetAcceptance() {
    explicitAcceptance = false;
    checkbox.checked = false;
    checkbox.disabled = mode !== "binding";
    consentRow.setAttribute("aria-disabled", String(checkbox.disabled));
  }

  function syncAction() {
    const binding = mode === "binding" && bindingAvailable();
    confirm.disabled = !binding || !explicitAcceptance || !checkbox.checked || attempted || inFlight;
    confirm.setAttribute("aria-disabled", String(confirm.disabled));
    confirm.textContent = inFlight
      ? copy.processingLabel
      : binding
        ? copy.confirmLabel
        : copy.lockedActionLabel;
  }

  function setMode(nextMode) {
    mode = nextMode === "binding" && bindingAvailable() ? "binding" : "preview";
    dialog.dataset.mode = mode;
    title.textContent = mode === "binding" ? copy.bindingTitle : copy.previewTitle;
    notice.textContent = mode === "binding" ? copy.bindingNotice : copy.previewNotice;
    status.textContent = mode === "binding" ? "Agreement is unchecked. Review the policy and explicitly agree to continue." : copy.previewNotice;
    attempted = false;
    inFlight = false;
    dialog.removeAttribute("aria-busy");
    resetAcceptance();
    syncAction();
  }

  function restoreFocus() {
    const target = invoker;
    invoker = null;
    if (target instanceof HTMLElement && target.isConnected && !target.hasAttribute("disabled")) {
      target.focus({ preventScroll: true });
    }
  }

  function close(reason = "cancel") {
    if (!dialog.open) return;
    dialog.close(reason);
  }

  function open({ intent = "preview", trigger = document.activeElement } = {}) {
    if (destroyed) throw new Error("The Terms review dialog has been destroyed.");
    if (dialog.open) close("reopen");
    invoker = trigger instanceof HTMLElement ? trigger : document.activeElement;
    setMode(intent);
    dialog.showModal();
    queueMicrotask(() => closeButton.focus({ preventScroll: true }));
    return getState();
  }

  async function attemptConfirm(event) {
    if (
      event?.isTrusted !== true
      || mode !== "binding"
      || !bindingAvailable()
      || !explicitAcceptance
      || !checkbox.checked
      || attempted
      || inFlight
    ) return false;

    attempted = true;
    inFlight = true;
    checkbox.disabled = true;
    consentRow.setAttribute("aria-disabled", "true");
    dialog.setAttribute("aria-busy", "true");
    status.textContent = copy.processingLabel;
    syncAction();

    try {
      const result = await onConfirm(Object.freeze({
        consent: Object.freeze({ accepted: true, ...descriptor }),
      }));
      if (result === false) throw new Error("The submission handler did not accept the account request.");
      close("confirmed");
      return true;
    } catch {
      inFlight = false;
      dialog.removeAttribute("aria-busy");
      resetAcceptance();
      status.textContent = copy.errorMessage;
      syncAction();
      return false;
    }
  }

  function onCheckboxChange(event) {
    if (event.isTrusted !== true) {
      resetAcceptance();
      status.textContent = "Agreement is unchecked. No account request or verification email has been sent.";
      syncAction();
      return;
    }
    explicitAcceptance = checkbox.checked;
    status.textContent = explicitAcceptance
      ? `Ready to accept ${descriptor.policyId} version ${descriptor.version}, revision ${descriptor.contentRevision}.`
      : "Agreement is unchecked. No account request or verification email has been sent.";
    syncAction();
  }

  function onDialogClick(event) {
    if (event.target === dialog) close("backdrop");
  }

  function onDialogCancel(event) {
    event.preventDefault();
    close("escape");
  }

  function onDialogClose() {
    mode = "preview";
    attempted = false;
    inFlight = false;
    dialog.removeAttribute("aria-busy");
    resetAcceptance();
    syncAction();
    queueMicrotask(restoreFocus);
  }

  checkbox.addEventListener("change", onCheckboxChange);
  confirm.addEventListener("click", attemptConfirm);
  closeButton.addEventListener("click", () => close("close"));
  cancelButton.addEventListener("click", () => close("cancel"));
  dialog.addEventListener("click", onDialogClick);
  dialog.addEventListener("cancel", onDialogCancel);
  dialog.addEventListener("close", onDialogClose);
  setMode("preview");

  function getState() {
    return Object.freeze({
      open: dialog.open,
      mode,
      bindingAvailable: bindingAvailable(),
      policy: descriptor,
      accepted: false,
      inFlight,
    });
  }

  return Object.freeze({
    open,
    close,
    getState,
    destroy() {
      if (destroyed) return;
      destroyed = true;
      if (dialog.open) dialog.close("destroy");
      checkbox.removeEventListener("change", onCheckboxChange);
      confirm.removeEventListener("click", attemptConfirm);
      dialog.removeEventListener("click", onDialogClick);
      dialog.removeEventListener("cancel", onDialogCancel);
      dialog.removeEventListener("close", onDialogClose);
      host.remove();
    },
  });
}




